/**
 * Política de senha, PURA.
 *
 * Fica no domínio e não na rota porque a mesma regra vale em três caminhos que
 * já existem ou vão existir: a troca pelo próprio usuário, o primeiro acesso
 * por convite e o `ROLE=senha` da linha de comando. Regra copiada em três
 * lugares diverge em um ano, e o lugar que divergir vira o buraco.
 */
/** Comprimento mínimo do NIST SP 800-63B para senha memorizada. */
export const MINIMO = 12;
export const MAXIMO = 256;
/**
 * Lista curta e deliberada. Uma lista de 100 mil senhas vazadas é a resposta
 * certa, e ela não cabe em memória de container: entra como serviço quando
 * houver. Enquanto isso, estas cobrem o que aparece de verdade num sistema
 * brasileiro de RH.
 */
const COMUNS = new Set([
    "123456789012", "senha123456", "password1234", "qwertyuiop12",
    "1234567890ab", "administrador", "entur1234567", "mudar123456",
    "trocar123456", "primeiroacesso", "departamentopessoal",
]);
const semAcento = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
/**
 * Recusa senha que CONTÉM um pedaço grande do nome ou do CPF.
 *
 * Num sistema de RH esse é o caso real: a pessoa usa o próprio primeiro nome
 * ou os dígitos do CPF, que é justamente o dado que quem tenta invadir já tem
 * na mão, porque é o mesmo campo do login.
 */
function pedacoDeDadoPessoal(senha, nome, cpf) {
    const s = semAcento(senha);
    const partes = semAcento(nome).split(/\s+/).filter((p) => p.length >= 4);
    if (partes.some((p) => s.includes(p)))
        return "nome";
    const digitos = senha.replace(/\D/g, "");
    // 6 dígitos seguidos do CPF já identificam a pessoa dentro da empresa.
    if (digitos.length >= 6 && cpf.length === 11) {
        for (let i = 0; i + 6 <= 11; i++) {
            if (digitos.includes(cpf.slice(i, i + 6)))
                return "cpf";
        }
    }
    return null;
}
/** Uma letra repetida ou uma sequência óbvia não é senha, é preenchimento. */
function ehRepetitiva(senha) {
    if (/^(.)\1+$/.test(senha))
        return true;
    const s = senha.toLowerCase();
    const ordenada = "abcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i + MINIMO <= ordenada.length; i++) {
        const trecho = ordenada.slice(i, i + MINIMO);
        if (s.includes(trecho) || s.includes([...trecho].reverse().join("")))
            return true;
    }
    return false;
}
export function avaliarNovaSenha(nova, contexto) {
    // Ordem importa: "curta" antes de tudo, porque é a recusa que a pessoa
    // consegue corrigir sem adivinhar, e mostrar três motivos de uma vez faz
    // ninguém ler nenhum.
    if (nova.length < MINIMO)
        return { ok: false, recusa: { motivo: "curta", minimo: MINIMO } };
    if (nova.length > MAXIMO)
        return { ok: false, recusa: { motivo: "longa", maximo: MAXIMO } };
    if (contexto.igualAAtual)
        return { ok: false, recusa: { motivo: "igualAAtual" } };
    if (ehRepetitiva(nova))
        return { ok: false, recusa: { motivo: "repetitiva" } };
    if (COMUNS.has(semAcento(nova)))
        return { ok: false, recusa: { motivo: "comum" } };
    const campo = pedacoDeDadoPessoal(nova, contexto.nome, contexto.cpf);
    if (campo)
        return { ok: false, recusa: { motivo: "contemDadoPessoal", campo } };
    return { ok: true };
}
/**
 * Frase de recusa, em PT-BR e sem travessão.
 *
 * Fica aqui e não na tela porque o mesmo texto é usado pela API e pela linha de
 * comando, e porque a lição do CRM foi que `cause` de erro não é serializado:
 * a tela reconhecia bloqueio pelo TEXTO e quebrava quando o texto mudava.
 */
export function explicar(r) {
    switch (r.motivo) {
        case "curta": return `A senha precisa de pelo menos ${r.minimo} caracteres.`;
        case "longa": return `A senha passou de ${r.maximo} caracteres.`;
        case "igualAAtual": return "A senha nova precisa ser diferente da atual.";
        case "repetitiva": return "Essa senha é uma sequência. Use algo que só você saiba.";
        case "comum": return "Essa senha é conhecida e já aparece em lista de vazamento.";
        case "contemDadoPessoal":
            return r.campo === "nome"
                ? "A senha não pode conter o seu nome, que é dado público dentro da empresa."
                : "A senha não pode conter dígitos do seu CPF, que é o mesmo número do seu login.";
    }
}
//# sourceMappingURL=senha.js.map